"use client";

import { Bars2Icon } from "@heroicons/react/24/outline";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";

import AccountMenu from "@/components/nav/AccountMenu";
import CoachStrip from "@/components/nav/CoachStrip";
import HelpButton from "@/components/nav/HelpButton";
import MobileNavMenu from "@/components/nav/MobileNavMenu";
import NavCoachPane from "@/components/nav/NavCoachPane";
import {
  BookmarkStar,
  bookmarksDrawerSection,
  bookmarksPanelView,
} from "@/components/nav/NavBookmarks";
import NavPanel from "@/components/nav/NavPanel";
import NavRail from "@/components/nav/NavRail";
import NavSearchDialog from "@/components/nav/NavSearchDialog";
import NavSearchPane from "@/components/nav/NavSearchPane";
import NavTopBar from "@/components/nav/NavTopBar";
import SabineLead from "@/components/SabineLead";
import SearchShortcutsTip from "@/components/nav/SearchShortcutsTip";
import {
  SideNavSlotsProvider,
  useSideNavSlots,
} from "@/components/nav/SideNavSlots";
import { useStudioBranding } from "@/components/nav/useStudioBranding";
import { useStudioNavData } from "@/components/nav/useStudioNavData";
import { useWorkspaceSwitcher } from "@/components/nav/useWorkspaceSwitcher";
import { useCurrentHash } from "@/hooks/useCurrentHash";
import { NAV_BELOW_TOP_BAR, NAV_ICON_BUTTON } from "@/components/nav/nav-styles";
import { useHelpSheet } from "@/components/nav/useHelpSheet";
import { BOOKMARKS_KEY, useNavBookmarks } from "@/components/nav/useNavBookmarks";
import { usePanelPreference } from "@/components/nav/useNavFrameState";
import { useNavCoach } from "@/components/nav/useNavCoach";
import { useNavColumnMotion } from "@/components/nav/useNavColumnMotion";
import { useNavShortcuts } from "@/components/nav/useNavShortcuts";
import { useNavSearch } from "@/components/nav/useNavSearch";
import { useNavTypeToSearch } from "@/components/nav/useNavTypeToSearch";
import { WorkspaceLogo } from "@/components/nav/WorkspaceSwitcher";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useApplePlatform } from "@/hooks/useApplePlatform";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { SABINE_NAME } from "@/lib/sabine";
import {
  buildNavSections,
  collectNavShortcuts,
  createNavContext,
  hasVisibleNavItems,
  resolveActiveNavKey,
} from "@/lib/navigation/nav-items";
import { stripNavigationLocalePrefix } from "@/lib/navigation/is-nav-item-active";
import { openNavSearch } from "@/lib/navigation/nav-search-open";
import { isSimulatorRoute } from "@/lib/simulatorRoutes";
import { isTrainingModeRestrictedUser } from "@/lib/training-mode-users";
import { cn } from "@/lib/utils";

const OUTLINE_KEY = "outline";
// Where the icon rail shows (Tailwind's `md`). Search opens as a pane beside
// it; below this there is no rail, so search opens as a dialog instead.
const RAIL_SCREEN = "(min-width: 768px)";
// A cream column beside the rail (the panel-and-search box, Sabine's pane)
// owns the corner where it meets the navy and the sheet, and runs under the
// sheet's edge so that corner is geometry (`.nav-column` in app/globals.css).
// Every column is named here (sideNavFrame.test.js).
const CREAM_CORNER = "nav-column";

const StudioLogo = ({ src, name }) => (
  <WorkspaceLogo workspace={{ name, image: src }} compact />
);

/** Signed-out pages keep a plain top bar: the studio's mark and name. */
function SignedOutBar({ displayName, displayLogo, onOpenCoach, inert }) {
  return (
    <header
      inert={inert}
      className="flex items-center gap-3 bg-surface-chrome px-4 py-2.5 md:px-6"
    >
      <Link href="/" className="flex min-w-0 items-center gap-3">
        <StudioLogo src={displayLogo} name={displayName} />
        <span className="truncate text-lg font-bold text-gray-700">
          {displayName || "Salesably — Execution Excellence for Sales Teams"}
        </span>
      </Link>
      {/* Below `md` the strip is hidden, so the coach opens from here. */}
      {onOpenCoach && (
        <CoachIconButton onClick={onOpenCoach} className="ml-auto md:hidden" />
      )}
    </header>
  );
}

/** Sabine's face as an icon button: the coach's opener where the strip is not. */
const CoachIconButton = ({ onClick, className }) => (
  <button
    type="button"
    aria-label={`Ask ${SABINE_NAME}`}
    onClick={onClick}
    className={cn(NAV_ICON_BUTTON, className)}
  >
    <SabineLead size="sm" />
  </button>
);

function MobileHeader({
  logoSrc,
  displayName,
  user,
  menuOpen,
  onOpenMenu,
  onOpenCoach,
  help,
  inert,
}) {
  return (
    <header
      inert={inert}
      data-app-nav
      className="flex items-center justify-between gap-3 px-4 py-2 md:hidden print:hidden"
    >
      <Link href="/" className="flex min-w-0 items-center gap-2">
        <StudioLogo src={logoSrc} name={displayName} />
        <span className="truncate text-sm font-semibold text-gray-900">
          {displayName}
        </span>
      </Link>
      <div className="flex items-center gap-1">
        {onOpenCoach && <CoachIconButton onClick={onOpenCoach} />}
        {help && <HelpButton compact open={help.open} onClick={help.onClick} />}
        <button
          type="button"
          aria-label="Search"
          onClick={() => openNavSearch()}
          className={NAV_ICON_BUTTON}
        >
          <Search className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          onClick={onOpenMenu}
          className={NAV_ICON_BUTTON}
        >
          <Bars2Icon className="h-6 w-6" />
        </button>
        <AccountMenu user={user} side="bottom" />
      </div>
    </header>
  );
}

const menuView = (menu, child, rowAction) =>
  menu && {
    key: menu.key,
    title: menu.menuLabel,
    subtitle: menu.subtitle,
    items: menu.items,
    crossLink: menu.crossLink,
    child,
    rowAction,
  };

/**
 * The app's navigation: a navy frame (a top bar and an icon rail with one
 * button per menu) around a cream panel listing the selected menu's links,
 * and the page content on a white sheet to their right. A training-mode user
 * gets the same frame with a single menu (`TRAINING_MODE_NAV`), which
 * `buildNavSections` hands back for them. The bar (NavTopBar) holds the
 * studio's mark, search, Ask Sabine and Help; the account menu lives on the
 * rail. Below `md` the frame becomes a cream header with a drawer.
 *
 * What the menus contain is declared once in `lib/navigation/nav-manifest.js`
 * and resolved for this viewer by `lib/navigation/nav-items.js`; this file
 * only gathers the session, studio and module state those need and arranges
 * the pieces. Adding a nav link belongs in the manifest, not here.
 *
 * The panel shows the menu the route belongs to, or the page's own outline
 * when it registered one (SideNavSlots). A rail button, or the menu's name
 * in the outline's header, shows that menu instead; pressing the rail button
 * again hides the panel. The choice lasts until the route changes.
 *
 * A page that registered a coach (its Sabine chat) gets the Ask button in the
 * bar; the chat opens beside the rail in the panel's place, with its header
 * in the bar above it, so search, panel and chat are one slot: opening any
 * one closes the others. Signed-out pages that register one (the shared
 * playbook) get a cream strip with the Ask button and the pane without the
 * rail. Anyone signed in also gets Help in the bar (and in the phone header);
 * its sheet floats over the page's right edge, outside that slot, so it can
 * stay open beside the chat.
 */
function SideNavFrame({ children }) {
  const { data: session, update } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const normalizedPathname = stripNavigationLocalePrefix(pathname || "");
  const isSimulator = isSimulatorRoute(normalizedPathname);

  const [currentHash, setCurrentHash] = useCurrentHash(pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  const [panelOpen, setPanelOpen] = usePanelPreference();
  // In a live simulation the panel starts hidden and a peek is not remembered.
  const [peekOpen, setPeekOpen] = useState(false);
  const [pinnedKey, setPinnedKey] = useState(null);
  const searchButtonRef = useRef(null);
  const search = useNavSearch({ triggerRef: searchButtonRef });
  const { setOpen: setSearchOpen } = search;
  // The bar's elements the search pane renders its input into (NavSearchRow)
  // and the chat its header into (NavTopBar).
  const [searchInputSlot, setSearchInputSlot] = useState(null);
  const [chatHeaderSlot, setChatHeaderSlot] = useState(null);
  const hasRail = useMediaQuery(RAIL_SCREEN);
  const apple = useApplePlatform();
  const { topAction, outline, coach: coachSlot } = useSideNavSlots();
  const coach = useNavCoach({ coach: coachSlot, hasRail, pathname });
  const helpSheet = useHelpSheet(Boolean(session));

  useEffect(() => {
    setMenuOpen(false);
    setPeekOpen(false);
    setPinnedKey(null);
    setSearchOpen(false);
  }, [pathname, setSearchOpen]);

  const { displayName, displayLogo } = useStudioBranding({
    session,
    searchParams,
    normalizedPathname,
  });
  const { enabledModules, isCommunityLeader, modules } =
    useStudioNavData(session);
  const switcherState = useWorkspaceSwitcher({ session, update });

  const user = session?.user;
  const workspace = session?.workspace;
  const canSwitchStudios = Boolean(user?.superUser || user?.role === "admin");
  // A training-mode user's one menu lists their home already, and "/" would
  // only bounce them there, so the rail and the drawer leave Home out.
  const showHome = !isTrainingModeRestrictedUser(user);

  const menus = buildNavSections(
    createNavContext({
      user,
      workspace,
      enabledModules,
      modules,
      isCommunityLeader,
    }),
  ).filter((menu) => hasVisibleNavItems(menu.items));

  // A training-mode user's nav is deliberately one menu, so no bookmarks.
  const query = searchParams?.toString();
  const bookmarks = useNavBookmarks({
    workspaceId: showHome ? user?.workspaceId : null,
    userId: user?.id,
    menus,
    currentHref: `${pathname || ""}${query ? `?${query}` : ""}${currentHash ? `#${currentHash}` : ""}`,
  });
  const starLink = bookmarks.enabled
    ? (link) => <BookmarkStar link={link} bookmarks={bookmarks} />
    : undefined;

  // Option/Alt + a letter goes straight to that page. The tour that teaches it
  // points at the first menu that has any, and quotes that menu's letters.
  const shortcuts = session ? collectNavShortcuts(menus) : [];
  useNavShortcuts(shortcuts);
  const tipMenuRef = useRef(null);
  const tipMenuKey = shortcuts[0]?.menuKey;

  // Typing at the nav opens search with the shown menu's pages first.
  const activeMenuKey = resolveActiveNavKey(menus, normalizedPathname);
  const shownMenu =
    menus.find((menu) => menu.key === pinnedKey) ||
    menus.find((menu) => menu.key === activeMenuKey);
  const typeToSearchProps = useNavTypeToSearch(shownMenu?.menuLabel || "");

  // Opening search (the row, ⌘K, typing at the nav) takes the chat's place,
  // in the same frame: the chat is shown only while search is closed.
  useEffect(() => {
    if (search.open) coach.setOpen(false);
  }, [search.open, coach.setOpen]);
  const coachShown = coach.open && !search.open;

  // The outline stands in for the route's menu, so its header names that
  // menu as the way back; a rail button leads the same way. While the page
  // is open that menu names the outline as the way forward, so the trail
  // reads "Learn › Playbook" from either side.
  const activeMenu = menus.find((menu) => menu.key === activeMenuKey);
  const outlineView = outline && {
    key: OUTLINE_KEY,
    title: outline.title,
    items: outline.links,
    activeHrefs: outline.activeHrefs || [],
    parent: activeMenu && { key: activeMenu.key, label: activeMenu.menuLabel },
  };
  const pinnedMenu = menus.find((menu) => menu.key === pinnedKey);
  const view =
    (pinnedKey === BOOKMARKS_KEY && bookmarks.enabled && bookmarksPanelView(bookmarks)) ||
    (pinnedKey !== OUTLINE_KEY &&
      menuView(
        pinnedMenu,
        outline && pinnedMenu === activeMenu
          ? { key: OUTLINE_KEY, label: outline.title }
          : undefined,
        starLink,
      )) ||
    outlineView ||
    menuView(activeMenu, undefined, starLink);

  const panelVisible = Boolean(view) && (isSimulator ? peekOpen : panelOpen);
  // The search pane or the coach takes the panel's place while open; the
  // panel's own state is untouched, so closing either brings the panel back
  // as it was.
  const panelShown = panelVisible && !search.open && !coachShown;
  const columnMotion = useNavColumnMotion(
    (search.open && hasRail && "search") ||
      (coachShown && "chat") ||
      (panelShown && "panel") ||
      null,
  );

  const coachPane = coach.registered && (
    <NavCoachPane
      open={coachShown}
      fullscreen={coach.fullscreen}
      onClose={() => coach.setOpen(false)}
      onFullscreenChange={hasRail ? coach.setFullscreen : null}
      headerSlot={hasRail ? chatHeaderSlot : null}
      className={session ? cn(NAV_BELOW_TOP_BAR, CREAM_CORNER) : undefined}
    >
      {coachSlot.chat}
    </NavCoachPane>
  );
  // Every Ask button closes search and the drawer first: search closes the
  // chat when it opens, so none of them ever show together.
  const openCoach = coach.registered
    ? () => {
        search.close();
        setMenuOpen(false);
        coach.setOpen(true);
      }
    : null;
  // A fullscreen chat is modal: everything else in the frame goes inert, so
  // Tab and a screen reader stay inside it.
  const behindCoach = coach.fullscreen || undefined;
  if (!session) {
    return (
      <>
        <SignedOutBar
          displayName={displayName}
          displayLogo={displayLogo}
          onOpenCoach={openCoach}
          inert={behindCoach}
        />
        <div className="flex min-h-0 grow flex-col md:flex-row">
          {coachPane}
          <div inert={behindCoach} className="flex min-h-0 min-w-0 grow flex-col">
            {coach.registered && (
              <CoachStrip
                placeholder={coachSlot.placeholder}
                open={coach.open}
                onOpen={openCoach}
              />
            )}
            {children}
          </div>
        </div>
      </>
    );
  }

  const showPanel = (next) =>
    isSimulator ? setPeekOpen(next) : setPanelOpen(next);
  const selectMenu = (key) => {
    search.close();
    coach.setOpen(false);
    if (panelVisible && view?.key === key) {
      setPinnedKey(null);
      showPanel(false);
      return;
    }
    setPinnedKey(key);
    showPanel(true);
  };
  const togglePanel = () => {
    if (search.open) {
      search.close();
      return;
    }
    if (coachShown) {
      coach.setOpen(false);
      return;
    }
    if (panelVisible) setPinnedKey(null);
    showPanel(!panelVisible);
  };

  const studioName = workspace?.name || displayName;
  const logoSrc = workspace?.image || displayLogo;
  const drawerSections = [
    bookmarks.enabled && bookmarksDrawerSection(bookmarks),
    outline && {
      label: outline.title,
      subtitle: "On this page",
      items: outline.links,
      activeHrefs: outline.activeHrefs || [],
    },
    ...menus.map((menu) => ({
      label: menu.menuLabel,
      subtitle: menu.subtitle,
      items: menu.items,
    })),
  ].filter(Boolean);

  return (
    <TooltipProvider delayDuration={300}>
      <div
        data-nav-motion={columnMotion}
        className="flex min-h-0 grow flex-col bg-surface-chrome md:bg-surface-chrome-ink"
      >
        <MobileHeader
          logoSrc={logoSrc}
          displayName={studioName}
          user={user}
          menuOpen={menuOpen}
          onOpenMenu={() => setMenuOpen(true)}
          onOpenCoach={openCoach}
          help={helpSheet.help}
          inert={behindCoach}
        />
        <MobileNavMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          pathname={pathname}
          currentHash={currentHash}
          onNavigateHash={setCurrentHash}
          sections={drawerSections}
          home={showHome}
        />
        <NavTopBar
          inert={behindCoach}
          studio={{
            displayName: studioName,
            displayLogo: logoSrc,
            switcher:
              canSwitchStudios && switcherState.availableWorkspaces.length > 0
                ? {
                    currentWorkspace: switcherState.switcherCurrentWorkspace,
                    workspaces: switcherState.availableWorkspaces,
                    value: switcherState.selectedWorkspaceId,
                    isSwitching: switcherState.isSwitchingWorkspace,
                    onSelect: switcherState.handleWorkspaceSwitch,
                  }
                : null,
          }}
          search={{
            open: search.open,
            buttonRef: searchButtonRef,
            inputSlotRef: setSearchInputSlot,
            onOpen: () => setSearchOpen(true),
          }}
          ask={
            openCoach && {
              placeholder: coachSlot.placeholder,
              onOpen: openCoach,
            }
          }
          help={helpSheet.help}
          chatOpen={coachShown}
          chatHeaderSlotRef={setChatHeaderSlot}
        />

        <div className="flex min-h-0 grow flex-col md:flex-row">
          <nav
            {...typeToSearchProps}
            inert={behindCoach}
            data-app-nav
            aria-label="Main navigation"
            className={cn(
              "nav-columns hidden md:flex md:shrink-0 print:hidden",
              NAV_BELOW_TOP_BAR,
            )}
          >
            <NavRail
              menus={menus}
              currentKey={activeMenuKey}
              openKey={panelShown ? view.key : null}
              onSelectMenu={selectMenu}
              isHome={normalizedPathname === "/"}
              home={showHome}
              bookmarks={bookmarks.enabled}
              user={user}
              panelOpen={panelShown}
              panelAvailable={Boolean(view) || search.open || coachShown}
              onTogglePanel={togglePanel}
              menuButtonRef={tipMenuRef}
              tipMenuKey={tipMenuKey}
            />
            <div className={cn("relative flex min-h-0 bg-surface-chrome", CREAM_CORNER)}>
              {view && (
                <NavPanel
                  open={panelShown}
                  view={view}
                  topAction={view.key === activeMenuKey ? topAction : null}
                  pathname={pathname}
                  currentHash={currentHash}
                  onNavigateHash={setCurrentHash}
                  onSelectMenu={selectMenu}
                />
              )}
              <NavSearchPane
                search={search}
                open={search.open && hasRail}
                apple={apple}
                inputSlot={searchInputSlot}
              />
            </div>
          </nav>
          {coachPane}
          {helpSheet.sheet}
          {/* `hasRail` is false until its effect runs, so the dialog is briefly
              mounted on a wide screen too — closed, because every way to open
              search is wired by an effect of the same commit. */}
          {!hasRail && <NavSearchDialog search={search} apple={apple} />}

          <SearchShortcutsTip
            searchButtonRef={searchButtonRef}
            menuRef={tipMenuRef}
            menuShortcuts={shortcuts.filter((s) => s.menuKey === tipMenuKey)}
            // Home only: other pages run spotlight tours of their own, and two at
            // once would stack.
            enabled={normalizedPathname === "/"}
          />

          <div inert={behindCoach} className="flex min-h-0 min-w-0 grow flex-col">
            {/* overflow-x-clip, not -hidden: hidden would make the sheet a
                scroll container and strand every `sticky` header inside it. */}
            {/* The sheet starts where the bar ends, on one line with the
                cream beside it, so the bar's controls have the same 10px of
                navy above and below them and nothing moves when a panel opens.
                It sits on navy whatever is open: the open column runs under
                its corner and paints the cream there (CREAM_CORNER). */}
            <div className="flex min-h-0 min-w-0 grow flex-col overflow-x-clip bg-white md:rounded-tl-3xl print:rounded-none">
              {children}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default function SideNav({ children }) {
  return (
    <SideNavSlotsProvider>
      <SideNavFrame>{children}</SideNavFrame>
    </SideNavSlotsProvider>
  );
}
